import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

export default function CarteClients({ user, isManager, onClose }) {
  const [clients, setClients] = useState([]);
  const { lang, isRTL } = useLang();
  const [visites, setVisites] = useState([]);
  const [clientSelectionne, setClientSelectionne] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtreComm, setFiltreComm] = useState('Tous');
  const [commerciaux, setCommerciaux] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const COULEURS_STATUT = {
    'Prospect': '#854F0B',
    'Client actif': '#3B6D11',
    'Client inactif': '#888888',
    'Nouveau client': '#185FA5',
  };

  useEffect(() => {
    charger();
    chargerLeaflet();
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markersRef.current = [];
    };
  }, []);

  const chargerLeaflet = () => {
    if (window.L) { setMapReady(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setMapReady(true);
    document.head.appendChild(script);
  };

  const charger = async () => {
    setLoading(true);
    try {
      let cSnap;
      if (isManager) {
        cSnap = await getDocs(collection(db, 'clients'));
        const uSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
        setCommerciaux(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else {
        cSnap = await getDocs(query(collection(db, 'clients'), where('commercialIds', 'array-contains', user.uid)));
      }
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const vSnap = await getDocs(collection(db, 'visites'));
      setVisites(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const clientsFiltres = clients.filter(c => {
    if (!c.gps) return false;
    if (filtreComm === 'Tous') return true;
    return c.commercialIds?.includes(filtreComm);
  });

  useEffect(() => {
    if (!mapReady || !mapRef.current || loading) return;
    if (clientsFiltres.length === 0) return;

    // Nettoyer l'ancienne instance si le DOM a changé
    if (mapInstanceRef.current && !mapRef.current.contains(mapInstanceRef.current.getContainer())) {
      try { mapInstanceRef.current.remove(); } catch (e) {}
      mapInstanceRef.current = null;
    }

    if (!mapInstanceRef.current) {
      const centre = clientsFiltres[0]?.gps || { lat: 36.365, lng: 6.614 };
      mapInstanceRef.current = window.L.map(mapRef.current).setView([centre.lat, centre.lng], 12);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(mapInstanceRef.current);
    }

    markersRef.current.forEach(m => mapInstanceRef.current.removeLayer(m));
    markersRef.current = [];

    const bounds = [];

    clientsFiltres.forEach(client => {
      const couleur = COULEURS_STATUT[client.type || client.statut] || '#C0392B';
      const nbVisites = visites.filter(v => v.clientId === client.id).length;
      const icon = window.L.divIcon({
        html: `<div style="width:${nbVisites > 0 ? 16 : 12}px;height:${nbVisites > 0 ? 16 : 12}px;border-radius:50%;background:${couleur};border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
        className: '',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });

      const marker = window.L.marker([client.gps.lat, client.gps.lng], { icon })
        .addTo(mapInstanceRef.current)
        .on('click', () => setClientSelectionne(client));

      markersRef.current.push(marker);
      bounds.push([client.gps.lat, client.gps.lng]);
    });

    if (bounds.length > 1) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [mapReady, loading, clientsFiltres.length, filtreComm]);

  const nbVisitesClient = (id) => visites.filter(v => v.clientId === id).length;
  const derniereVisite = (id) => {
    const vs = visites.filter(v => v.clientId === id).sort((a, b) => new Date(b.heureDebut) - new Date(a.heureDebut));
    return vs[0] ? new Date(vs[0].heureDebut).toLocaleDateString('fr-FR') : '-';
  };

  const clientsSansGps = clients.filter(c => !c.gps);

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>Carte des clients</div>
          <div style={styles.topbarSub}>{clientsFiltres.length} client(s) affiché(s)</div>
        </div>
        {clientsSansGps.length > 0 && (
          <div style={styles.warningBadge}>⚠️ {clientsSansGps.length} sans GPS</div>
        )}
      </div>

      {isManager && commerciaux.length > 0 && (
        <div style={styles.filtreBar}>
          <select style={styles.filtreSelect} value={filtreComm} onChange={e => { setFiltreComm(e.target.value); if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } }}>
            <option value="Tous">Tous les commerciaux</option>
            {commerciaux.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
          </select>
        </div>
      )}

      <div style={styles.legende}>
        {Object.entries(COULEURS_STATUT).map(([statut, couleur]) => (
          <div key={statut} style={styles.legendeItem}>
            <div style={{ ...styles.legendeDot, background: couleur }}></div>
            <span style={styles.legendeLabel}>{statut}</span>
          </div>
        ))}
      </div>

      <div style={styles.mapWrap}>
        {(loading || !mapReady) && (
          <div style={styles.mapLoading}>
            <div style={{ fontSize: 32 }}>🗺️</div>
            <div style={{ marginTop: 8, color: '#888' }}>Chargement de la carte...</div>
          </div>
        )}
        {!loading && mapReady && clientsFiltres.length === 0 && (
          <div style={styles.mapLoading}>
            <div style={{ fontSize: 32 }}>📍</div>
            <div style={{ marginTop: 8, color: '#888' }}>Aucun client avec GPS</div>
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Ajoutez des positions GPS dans le Dispatch</div>
          </div>
        )}
        <div ref={mapRef} style={{ width: '100%', height: '100%' }}></div>

        {clientSelectionne && (
          <div style={styles.ficheCard}>
            <div style={styles.ficheHeader}>
              <div style={styles.ficheAv}>{clientSelectionne.nom?.[0]?.toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={styles.ficheNom}>{clientSelectionne.nom}</div>
                <div style={styles.ficheSub}>{clientSelectionne.type || clientSelectionne.statut} · {clientSelectionne.commune}</div>
              </div>
              <button style={styles.ficheClose} onClick={() => setClientSelectionne(null)}>✕</button>
            </div>
            <div style={styles.ficheStats}>
              <div style={styles.ficheStat}>
                <div style={styles.ficheStatVal}>{nbVisitesClient(clientSelectionne.id)}</div>
                <div style={styles.ficheStatLabel}>Visites</div>
              </div>
              <div style={styles.ficheStat}>
                <div style={styles.ficheStatVal}>{derniereVisite(clientSelectionne.id)}</div>
                <div style={styles.ficheStatLabel}>Dernière visite</div>
              </div>
              <div style={styles.ficheStat}>
                <div style={styles.ficheStatVal}>{clientSelectionne.potentiel || 'Moyen'}</div>
                <div style={styles.ficheStatLabel}>Potentiel</div>
              </div>
            </div>
            {clientSelectionne.telephone && (
              <div style={styles.ficheTel}>📞 {clientSelectionne.telephone}</div>
            )}
            <button style={styles.ficheMapsBtn}
              onClick={() => window.open(`https://www.google.com/maps?q=${clientSelectionne.gps.lat},${clientSelectionne.gps.lng}`, '_blank')}>
              📍 Ouvrir dans Google Maps
            </button>
          </div>
        )}
      </div>

      <div style={styles.listeWrap}>
        <div style={styles.listeTitre}>Clients ({clientsFiltres.length})</div>
        <div style={styles.listeScroll}>
          {clientsFiltres.map((c) => (
            <div key={c.id} style={styles.listeItem}
              onClick={() => {
                setClientSelectionne(c);
                if (mapInstanceRef.current && c.gps) {
                  mapInstanceRef.current.setView([c.gps.lat, c.gps.lng], 15);
                }
              }}>
              <div style={{ ...styles.listePoint, background: COULEURS_STATUT[c.type || c.statut] || '#C0392B' }}></div>
              <div style={{ flex: 1 }}>
                <div style={styles.listeNom}>{c.nom}</div>
                <div style={styles.listeSub}>{nbVisitesClient(c.id)} visite(s)</div>
              </div>
              <span style={styles.listeArrow}>→</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', height: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  warningBadge: { background: '#FAEEDA', color: '#854F0B', fontSize: 11, padding: '4px 8px', borderRadius: 8, fontWeight: 500, flexShrink: 0 },
  filtreBar: { background: '#fff', padding: '8px 12px', borderBottom: '1px solid #eee', flexShrink: 0 },
  filtreSelect: { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 },
  legende: { display: 'flex', gap: 10, padding: '6px 12px', background: '#fff', borderBottom: '1px solid #eee', flexShrink: 0, flexWrap: 'wrap' },
  legendeItem: { display: 'flex', alignItems: 'center', gap: 4 },
  legendeDot: { width: 10, height: 10, borderRadius: '50%' },
  legendeLabel: { fontSize: 10, color: '#666' },
  mapWrap: { flex: 1, position: 'relative', background: '#e8e8e8', minHeight: 0 },
  mapLoading: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', zIndex: 10 },
  ficheCard: { position: 'absolute', bottom: 0, left: 0, right: 0, background: '#fff', borderRadius: '16px 16px 0 0', padding: '14px 16px 20px', boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', zIndex: 1000 },
  ficheHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  ficheAv: { width: 38, height: 38, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 600, color: '#185FA5', flexShrink: 0 },
  ficheNom: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  ficheSub: { fontSize: 12, color: '#888', marginTop: 2 },
  ficheClose: { background: '#f5f5f5', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', flexShrink: 0 },
  ficheStats: { display: 'flex', gap: 8, marginBottom: 10 },
  ficheStat: { flex: 1, background: '#f9f9f9', borderRadius: 8, padding: '8px', textAlign: 'center' },
  ficheStatVal: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  ficheStatLabel: { fontSize: 10, color: '#888', marginTop: 2 },
  ficheTel: { fontSize: 13, color: '#666', marginBottom: 10 },
  ficheMapsBtn: { width: '100%', padding: 11, background: '#1C2833', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  listeWrap: { background: '#fff', borderTop: '1px solid #eee', flexShrink: 0 },
  listeTitre: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', padding: '8px 12px 4px' },
  listeScroll: { overflowX: 'auto', display: 'flex', gap: 8, padding: '4px 12px 12px' },
  listeItem: { display: 'flex', alignItems: 'center', gap: 8, background: '#f9f9f9', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', flexShrink: 0, minWidth: 140 },
  listePoint: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  listeNom: { fontSize: 12, fontWeight: 600, color: '#1C2833' },
  listeSub: { fontSize: 10, color: '#888' },
  listeArrow: { fontSize: 12, color: '#aaa' },
};
